// src/core-pages/GL-draft.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTeam } from "../context/TeamContext";
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { db } from "../Security/firebase-config";
import { useNavigate } from "react-router-dom";
import Footer from "../components/core-components/Footer";
import CustomAlert from "../components/core-components/CustomAlert";
import MentionMenu from "../components/Review/MentionMenu";
import DeleteHighlightConfirm from "../components/core-components/DeleteHighlightConfirm";

import LoginTeamGate from "../components/core-components/LoginTeamGate";

import "./GuideLibrary.css";

function normalizeLocation(loc) {
  return String(loc || "").trim();
}

function safeStr(v) {
  return String(v ?? "");
}

// Keys used to determine if an item exists in personal/team/both
function highlightKey(h) {
  const loc = normalizeLocation(h?.location);
  const cid = safeStr(h?.containerId);
  const ex = safeStr(h?.excerpt);
  return `${loc}||${cid}||${ex}`;
}

function favoriteKey(f) {
  const loc = normalizeLocation(f?.pageLocation);
  const hid = safeStr(f?.headingId);
  const hc = safeStr(f?.headingContent);
  return `${loc}||${hid}||${hc}`;
}

function groupByLocation({ highlights = [], favorites = [] }) {
  const map = new Map();

  const ensure = (location) => {
    const key = normalizeLocation(location);
    if (!key) return null;
    if (!map.has(key)) {
      map.set(key, { location: key, highlights: [], favorites: [] });
    }
    return map.get(key);
  };

  (highlights || []).forEach((h) => {
    const bucket = ensure(h.location);
    if (!bucket) return;
    bucket.highlights.push(h);
  });

  (favorites || []).forEach((f) => {
    const bucket = ensure(f.pageLocation);
    if (!bucket) return;
    bucket.favorites.push(f);
  });

  for (const entry of map.values()) {
    entry.highlights.sort((a, b) =>
      safeStr(a.excerpt).localeCompare(safeStr(b.excerpt))
    );
    entry.favorites.sort((a, b) =>
      safeStr(a.headingContent).localeCompare(safeStr(b.headingContent))
    );
  }

  return Array.from(map.values()).sort((a, b) => a.location.localeCompare(b.location));
}

function uid() {
  // good enough for client-side IDs
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// Try to extract a list of usernames from common team doc shapes
function extractTeamUsernames(teamDoc) {
  const out = new Set();

  const addMaybe = (u) => {
    if (!u) return;
    out.add(String(u));
  };

  // common: teamDoc.members = [{ username }, ...] OR members = ["uid1","uid2"] (string uids won't help here)
  if (Array.isArray(teamDoc?.members)) {
    teamDoc.members.forEach((m) => {
      if (!m || typeof m !== "object") return;

      const u =
        m?.username ||
        m?.userName ||
        m?.displayName ||
        m?.name ||
        m?.nickname ||
        (typeof m?.email === "string" ? m.email.split("@")[0] : null);

      addMaybe(u);
    });
  }

  if (Array.isArray(teamDoc?.memberUsernames)) teamDoc.memberUsernames.forEach(addMaybe);
  if (Array.isArray(teamDoc?.usernames)) teamDoc.usernames.forEach(addMaybe);

  if (Array.isArray(teamDoc?.teamMembers)) {
    teamDoc.teamMembers.forEach((m) => {
      if (!m || typeof m !== "object") return;

      const u =
        m?.username ||
        m?.userName ||
        m?.displayName ||
        m?.name ||
        m?.nickname ||
        (typeof m?.email === "string" ? m.email.split("@")[0] : null);

      addMaybe(u);
    });
  }

  return Array.from(out).filter(Boolean).sort((a, b) => a.localeCompare(b));
}

export default function GuideLibrary({
  hideFooter = false,
  onNavigateToLocation = null,
} = {}) {
  const { userData, loading: authLoading } = useAuth();
  const { teamId: activeTeamId } = useTeam();
  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const [personal, setPersonal] = useState({
    highlights: [],
    favorites: [],
    username: "",
    libraryNotes: [],
  });

  const [team, setTeam] = useState({
    highlights: [],
    favorites: [],
    teamName: "",
    libraryNotes: [],
    _teamUsernames: [],
  });

  // Used to resolve team member usernames for @mentions
  const [teamMemberUids, setTeamMemberUids] = useState([]);
  const [resolvedTeamUsernames, setResolvedTeamUsernames] = useState([]);


  // which side is driving center view: "personal" | "team"
  const [activePane, setActivePane] = useState("personal");

  // selected location per pane
  const [selectedPersonalLocation, setSelectedPersonalLocation] = useState("");
  const [selectedTeamLocation, setSelectedTeamLocation] = useState("");

  // Notes UI state
  const [noteUI, setNoteUI] = useState({
    openFor: null, // { type: "highlight"|"favorite", key: string }
    saveTo: "personal", // "personal" | "team"
    text: "",
    editingNoteId: null,
  });

  // Mention menu state
  const [mentionState, setMentionState] = useState({
    open: false,
    items: [],
    activeIndex: 0,
    style: { top: 0, left: 0 },
    query: "",
    triggerIndex: -1, // index of '@' in textarea when menu opened
  });

  const noteTextareaRef = useRef(null);
  const mentionAnchorRef = useRef(null);

  // auto-dismiss alerts
  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setAlert(null), 2800);
    return () => clearTimeout(t);
  }, [alert]);

  // load personal once, and team live on activeTeamId
  useEffect(() => {
    if (!userData?.uid) {
      setLoadingData(false);
      setPersonal({ highlights: [], favorites: [], username: "", libraryNotes: [] });
      setTeam({ highlights: [], favorites: [], teamName: "", libraryNotes: [], _teamUsernames: [] });
      setTeamMemberUids([]);
      setResolvedTeamUsernames([]);
      return;
    }

    setLoadingData(true);

    let stopTeam = null;

    (async () => {
      // Personal
      try {
        const userSnap = await getDoc(doc(db, "users", userData.uid));
        const u = userSnap.exists() ? userSnap.data() : {};
        setPersonal({
          highlights: Array.isArray(u.highlights) ? u.highlights : [],
          favorites: Array.isArray(u.favorites) ? u.favorites : [],
          username: u.username || userData.username || "",
          libraryNotes: Array.isArray(u.libraryNotes) ? u.libraryNotes : [],
        });
      } catch (e) {
        console.warn("Failed to load personal library:", e);
      }

      // Team (live)
      if (activeTeamId) {
        const teamRef = doc(db, "teams", activeTeamId);
        stopTeam = onSnapshot(
          teamRef,
          (snap) => {
            if (!snap.exists()) {
              setTeam({ highlights: [], favorites: [], teamName: "", libraryNotes: [], _teamUsernames: [] });
              setTeamMemberUids([]);
              setResolvedTeamUsernames([]);
            } else {
                const t = snap.data();

                console.log("[GL] team doc keys:", Object.keys(t || {}));
                console.log("[GL] team doc sample:", t);

                console.log("[GL] members[0] sample:", Array.isArray(t?.members) ? t.members[0] : null);
                console.log("[GL] teamRoles keys sample:", t?.teamRoles ? Object.keys(t.teamRoles) : null);

                // ---- Extract member UIDs from many possible team doc shapes ----
                const uidSet = new Set();

                const pushId = (v) => {
                if (!v) return;

                // If it's a doc ref (or similar object with an .id)
                if (typeof v === "object" && v?.id && typeof v.id === "string") {
                    uidSet.add(v.id);
                    return;
                }

                uidSet.add(String(v));
                };

                // 1) members can be: ["uid1","uid2"] OR [{uid:"..."}, ...] OR [{userId:"..."}, ...]
                if (Array.isArray(t?.members)) {
                t.members.forEach((m) => {
                    if (typeof m === "string") {
                    pushId(m); // ✅ IMPORTANT: supports members as uid strings
                    return;
                    }
                    // object form
                    pushId(
                    m?.uid ||
                        m?.userId ||
                        m?.id ||
                        m?.memberId ||
                        m?.memberUid ||
                        m?.memberUID ||
                        m?.ref
                    );
                });
                }

                // 2) teamMembers variant
                if (Array.isArray(t?.teamMembers)) {
                t.teamMembers.forEach((m) => {
                    if (typeof m === "string") {
                    pushId(m);
                    return;
                    }
                    pushId(
                    m?.uid ||
                        m?.userId ||
                        m?.id ||
                        m?.memberId ||
                        m?.memberUid ||
                        m?.memberUID ||
                        m?.ref
                    );
                });
                }

                // 3) arrays of strings
                if (Array.isArray(t?.memberIds)) t.memberIds.forEach(pushId);
                if (Array.isArray(t?.memberUids)) t.memberUids.forEach(pushId);

                // 4) map/object forms
                if (t?.membersMap && typeof t.membersMap === "object") Object.keys(t.membersMap).forEach(pushId);
                if (t?.memberIdsMap && typeof t.memberIdsMap === "object") Object.keys(t.memberIdsMap).forEach(pushId);
                if (t?.memberUidsMap && typeof t.memberUidsMap === "object") Object.keys(t.memberUidsMap).forEach(pushId);

                // 5) ✅ HUGE FALLBACK: teamRoles is often { [uid]: "role" } or { [uid]: {...} }
                if (t?.teamRoles && typeof t.teamRoles === "object") {
                Object.keys(t.teamRoles).forEach(pushId);
                }

                // 6) Also include leader id if present
                if (t?.teamLeaderId) pushId(t.teamLeaderId);

                const allUids = Array.from(uidSet).filter(Boolean);

                console.log("[GL] extracted teamMemberUids:", allUids);

                setTeamMemberUids(allUids);
                // ---- END ----


                setTeam({
                    highlights: Array.isArray(t.highlights) ? t.highlights : [],
                    favorites: Array.isArray(t.favorites) ? t.favorites : [],
                    teamName: t.teamName || "",
                    libraryNotes: Array.isArray(t.libraryNotes) ? t.libraryNotes : [],
                    _teamUsernames: extractTeamUsernames(t), // still keep this if it exists
                });
            }

            setLoadingData(false);
          },
          () => setLoadingData(false)
        );
      } else {
        setTeam({ highlights: [], favorites: [], teamName: "", libraryNotes: [], _teamUsernames: [] });
        setLoadingData(false);
      }
    })();

    return () => {
      if (stopTeam) stopTeam();
    };
  }, [userData?.uid, userData?.username, activeTeamId]);

  const openDeleteConfirm = (message, onConfirm) => {
    setDeleteConfirm({
      open: true,
      message,
      onConfirm,
    });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      open: false,
      message: "",
      onConfirm: null,
    });
  };

  useEffect(() => {
      let cancelled = false;

      async function run() {
          if (!teamMemberUids.length) {
          setResolvedTeamUsernames([]);
          return;
          }

          try {
          const usernames = [];

          // Firestore "in" query max is 10 values, so batch.
          for (let i = 0; i < teamMemberUids.length; i += 10) {
              const batch = teamMemberUids.slice(i, i + 10);

              // assumes users collection doc IDs are the uid
              const qy = query(collection(db, "users"), where(documentId(), "in", batch));
              const snap = await getDocs(qy);

              snap.forEach((docSnap) => {
              const d = docSnap.data();
              if (d?.username) usernames.push(String(d.username));
              else if (d?.displayName) usernames.push(String(d.displayName));
              });
          }

          const uniqueSorted = Array.from(new Set(usernames)).sort((a, b) => a.localeCompare(b));
          if (!cancelled) setResolvedTeamUsernames(uniqueSorted);
          } catch (e) {
          console.warn("Failed to resolve team usernames:", e);
          if (!cancelled) setResolvedTeamUsernames([]);
          }
      }

      run();
      return () => {
          cancelled = true;
      };
  }, [teamMemberUids]);


  // group by location
  const personalLocations = useMemo(
    () => groupByLocation(personal),
    [personal]
  );

  const teamLocations = useMemo(
    () => groupByLocation(team),
    [team]
  );

  // pick default selections once data arrives
  useEffect(() => {
    if (!selectedPersonalLocation && personalLocations.length) {
      setSelectedPersonalLocation(personalLocations[0].location);
    }
  }, [personalLocations, selectedPersonalLocation]);

  useEffect(() => {
    if (!selectedTeamLocation && teamLocations.length) {
      setSelectedTeamLocation(teamLocations[0].location);
    }
  }, [teamLocations, selectedTeamLocation]);

  const activeLocation =
    activePane === "personal" ? selectedPersonalLocation : selectedTeamLocation;

  const activeData = useMemo(() => {
    const src = activePane === "personal" ? personalLocations : teamLocations;
    return src.find((x) => x.location === activeLocation) || null;
  }, [activePane, activeLocation, personalLocations, teamLocations]);

  // membership maps (personal/team/both) for badges + delete options
  const membership = useMemo(() => {
    const hPersonal = new Set((personal.highlights || []).map(highlightKey));
    const hTeam = new Set((team.highlights || []).map(highlightKey));
    const fPersonal = new Set((personal.favorites || []).map(favoriteKey));
    const fTeam = new Set((team.favorites || []).map(favoriteKey));

    return {
      highlight: (h) => {
        const k = highlightKey(h);
        const inPersonal = hPersonal.has(k);
        const inTeam = hTeam.has(k);
        return { inPersonal, inTeam, key: k };
      },
      favorite: (f) => {
        const k = favoriteKey(f);
        const inPersonal = fPersonal.has(k);
        const inTeam = fTeam.has(k);
        return { inPersonal, inTeam, key: k };
      },
    };
  }, [personal.highlights, team.highlights, personal.favorites, team.favorites]);

  const getOriginLabel = ({ inPersonal, inTeam }) => {
    if (inPersonal && inTeam) return "Saved in: Individual + Team";
    if (inPersonal) return "Saved in: only Individual database";
    if (inTeam) return "Saved in: only Team database";
    return "Saved in: unknown";
  };

  const handleNavigate = (location, containerIdOrHeadingId) => {
    if (!location) return;
    const qs = containerIdOrHeadingId ? `?highlight=${containerIdOrHeadingId}` : "";
    const fullPath = `${location}${qs}`;

    if (typeof onNavigateToLocation === "function") {
      onNavigateToLocation({
        location,
        query: qs,
        fullPath,
      });
      return;
    }

    navigate(fullPath);
  };

  const leftTitle = personal.username ? `${personal.username}'s Library` : "Your Library";
  const rightTitle = team.teamName ? `${team.teamName}'s Library` : "Team Library";

  // -------- NOTES: helpers --------

  const allNotes = useMemo(() => {
    // show notes from both sources under items (each note has implicit source by where it's stored)
    const personalNotes = (personal.libraryNotes || []).map((n) => ({ ...n, _source: "personal" }));
    const teamNotes = (team.libraryNotes || []).map((n) => ({ ...n, _source: "team" }));
    return [...personalNotes, ...teamNotes];
  }, [personal.libraryNotes, team.libraryNotes]);

  const notesForTarget = useMemo(() => {
    const index = new Map(); // key: `${type}||${targetKey}` -> [notes]
    for (const n of allNotes) {
      const k = `${n.targetType}||${n.targetKey}`;
      if (!index.has(k)) index.set(k, []);
      index.get(k).push(n);
    }
    // sort notes newest first
    for (const arr of index.values()) {
      arr.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
    }
    return index;
  }, [allNotes]);

  const getNotes = (type, targetKey) => {
    return notesForTarget.get(`${type}||${targetKey}`) || [];
  };

  const openNoteEditor = ({ type, targetKey, defaultSaveTo }) => {
    setNoteUI({
      openFor: { type, key: targetKey },
      saveTo: defaultSaveTo || (activePane === "team" ? "team" : "personal"),
      text: "",
      editingNoteId: null,
    });
    setMentionState((s) => ({ ...s, open: false, query: "", triggerIndex: -1 }));
  };

  const openEditNote = (note) => {
    setNoteUI({
      openFor: { type: note.targetType, key: note.targetKey },
      saveTo: note._source, // must edit in same source doc
      text: note.text || "",
      editingNoteId: note.id,
    });
    setMentionState((s) => ({ ...s, open: false, query: "", triggerIndex: -1 }));
  };

  const closeNoteEditor = () => {
    setNoteUI({ openFor: null, saveTo: "personal", text: "", editingNoteId: null });
    setMentionState((s) => ({ ...s, open: false, query: "", triggerIndex: -1 }));
  };

    const canSaveToTeam = !!activeTeamId;
    const mentionCandidates = useMemo(() => {
        const base = new Set([
            ...(team._teamUsernames || []),
            ...(resolvedTeamUsernames || []),
        ]);

        if (personal.username) base.add(personal.username);

        const list = Array.from(base).filter(Boolean).sort((a, b) => a.localeCompare(b));

        if (activePane === "team") {
            // keep your own username last (optional)
            const withoutSelf = list.filter((u) => u !== personal.username);
            return personal.username ? [...withoutSelf, personal.username] : withoutSelf;
        }

        return list;
    }, [team._teamUsernames, resolvedTeamUsernames, personal.username, activePane]);


    useEffect(() => {
        console.log("activeTeamId:", activeTeamId);
        console.log("teamMemberUids:", teamMemberUids);
        console.log("team._teamUsernames:", team._teamUsernames);
        console.log("resolvedTeamUsernames:", resolvedTeamUsernames);
        console.log("mentionCandidates:", mentionCandidates);
    }, [activeTeamId, teamMemberUids, team._teamUsernames, resolvedTeamUsernames, mentionCandidates]);


  const buildMentionItems = (q) => {
    const query = q.toLowerCase();
    const matches = mentionCandidates
      .filter((u) => u.toLowerCase().includes(query))
      .slice(0, 10)
      .map((u) => ({ id: `user_${u}`, label: u, kind: "user", payload: { roleLower: "player" } }));
    return matches;
  };

  const positionMentionMenu = () => {
    // lightweight positioning: near the textarea wrapper
    const anchor = mentionAnchorRef.current;
    if (!anchor) return { top: 0, left: 0 };
    const r = anchor.getBoundingClientRect();
    return { top: r.height + 6, left: 0 };
  };

  const onNoteTextChange = (next) => {
    // enforce 425 chars
    const text = next.slice(0, 425);
    setNoteUI((s) => ({ ...s, text }));

    // mention detection: last '@' after last whitespace/newline
    const caret = noteTextareaRef.current?.selectionStart ?? text.length;
    const upToCaret = text.slice(0, caret);

    const at = upToCaret.lastIndexOf("@");
    if (at === -1) {
      setMentionState((ms) => ({ ...ms, open: false, query: "", triggerIndex: -1 }));
      return;
    }

    // don't open if there's a space between '@' and caret
    const afterAt = upToCaret.slice(at + 1);
    if (afterAt.includes(" ") || afterAt.includes("\n") || afterAt.includes("\t")) {
      setMentionState((ms) => ({ ...ms, open: false, query: "", triggerIndex: -1 }));
      return;
    }

    const query = afterAt;
    // only allow mentions if saving to team OR if you still want it in personal
    // (you asked "if possible", so we'll allow regardless; team suggestions still work)
    const items = buildMentionItems(query);

    setMentionState((ms) => ({
      ...ms,
      open: true,
      items,
      activeIndex: 0,
      style: positionMentionMenu(),
      query,
      triggerIndex: at,
    }));
  };

  const pickMention = (item) => {
    const ta = noteTextareaRef.current;
    const text = noteUI.text;
    if (!ta) return;

    const caret = ta.selectionStart ?? text.length;
    const at = mentionState.triggerIndex;
    if (at < 0 || at > caret) return;

    const before = text.slice(0, at);
    const after = text.slice(caret);
    const insert = `@${item.label} `;
    const next = (before + insert + after).slice(0, 425);

    setNoteUI((s) => ({ ...s, text: next }));
    setMentionState((ms) => ({ ...ms, open: false, query: "", triggerIndex: -1 }));

    requestAnimationFrame(() => {
      const pos = Math.min((before + insert).length, 425);
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const saveNote = async () => {
    if (!userData?.uid) return;
    if (!noteUI.openFor) return;

    const trimmed = noteUI.text.trim();
    if (!trimmed) {
      setAlert({ type: "warning", message: "Note is empty." });
      return;
    }

    const isTeam = noteUI.saveTo === "team";
    if (isTeam && !activeTeamId) {
      setAlert({ type: "warning", message: "Join a Team to save team notes." });
      return;
    }

    const targetType = noteUI.openFor.type;
    const targetKey = noteUI.openFor.key;

    const now = Date.now();
    const createdBy = personal.username || userData.username || "Unknown";

    try {
      if (isTeam) {
        const ref = doc(db, "teams", activeTeamId);
        const existing = Array.isArray(team.libraryNotes) ? team.libraryNotes : [];

        let nextNotes;
        if (noteUI.editingNoteId) {
          nextNotes = existing.map((n) =>
            n.id === noteUI.editingNoteId ? { ...n, text: trimmed, updatedAt: now } : n
          );
        } else {
          nextNotes = [
            ...existing,
            {
              id: uid(),
              targetType,
              targetKey,
              text: trimmed,
              createdAt: now,
              updatedAt: now,
              createdBy,
            },
          ];
        }

        await updateDoc(ref, { libraryNotes: nextNotes });
        setAlert({ type: "success", message: noteUI.editingNoteId ? "Note updated." : "Note saved." });
      } else {
        const ref = doc(db, "users", userData.uid);
        const existing = Array.isArray(personal.libraryNotes) ? personal.libraryNotes : [];

        let nextNotes;
        if (noteUI.editingNoteId) {
          nextNotes = existing.map((n) =>
            n.id === noteUI.editingNoteId ? { ...n, text: trimmed, updatedAt: now } : n
          );
        } else {
          nextNotes = [
            ...existing,
            {
              id: uid(),
              targetType,
              targetKey,
              text: trimmed,
              createdAt: now,
              updatedAt: now,
              createdBy,
            },
          ];
        }

        await updateDoc(ref, { libraryNotes: nextNotes });
        setAlert({ type: "success", message: noteUI.editingNoteId ? "Note updated." : "Note saved." });
      }

      closeNoteEditor();
    } catch (e) {
      console.warn("Failed to save note:", e);
      setAlert({ type: "error", message: "Failed to save note." });
    }
  };

  const deleteNote = (note) => {
    if (!userData?.uid) return;

    openDeleteConfirm("Delete this note?", async () => {
      try {
        if (note._source === "team") {
          if (!activeTeamId) return;
          const ref = doc(db, "teams", activeTeamId);
          const existing = Array.isArray(team.libraryNotes) ? team.libraryNotes : [];
          const nextNotes = existing.filter((n) => n.id !== note.id);

          await updateDoc(ref, { libraryNotes: nextNotes });

          setTeam((prev) => ({
            ...prev,
            libraryNotes: nextNotes,
          }));

          setAlert({ type: "success", message: "Note deleted." });
        } else {
          const ref = doc(db, "users", userData.uid);
          const existing = Array.isArray(personal.libraryNotes) ? personal.libraryNotes : [];
          const nextNotes = existing.filter((n) => n.id !== note.id);

          await updateDoc(ref, { libraryNotes: nextNotes });

          setPersonal((prev) => ({
            ...prev,
            libraryNotes: nextNotes,
          }));

          setAlert({ type: "success", message: "Note deleted." });
        }

        closeDeleteConfirm();
      } catch (e) {
        console.warn("Failed to delete note:", e);
        setAlert({ type: "error", message: "Failed to delete note." });
        throw e;
      }
    });
  };

  // -------- DELETE highlight/favorite --------

  const deleteHighlightFrom = (where, h) => {
    if (!userData?.uid) return;

    openDeleteConfirm(
      `Delete this highlight from ${where === "team" ? "Team" : "Individual"}?`,
      async () => {
        try {
          if (where === "team") {
            if (!activeTeamId) return;
            const ref = doc(db, "teams", activeTeamId);
            const existing = Array.isArray(team.highlights) ? team.highlights : [];
            const k = highlightKey(h);
            const next = existing.filter((x) => highlightKey(x) !== k);

            await updateDoc(ref, { highlights: next });

            setTeam((prev) => ({
              ...prev,
              highlights: next,
            }));

            setAlert({ type: "success", message: "Deleted from Team." });
          } else {
            const ref = doc(db, "users", userData.uid);
            const existing = Array.isArray(personal.highlights) ? personal.highlights : [];
            const k = highlightKey(h);
            const next = existing.filter((x) => highlightKey(x) !== k);

            await updateDoc(ref, { highlights: next });

            setPersonal((prev) => ({
              ...prev,
              highlights: next,
            }));

            setAlert({ type: "success", message: "Deleted from Individual." });
          }

          closeDeleteConfirm();
        } catch (e) {
          console.warn("Failed to delete highlight:", e);
          setAlert({ type: "error", message: "Failed to delete highlight." });
          throw e;
        }
      }
    );
  };

  const deleteFavoriteFrom = (where, f) => {
    if (!userData?.uid) return;

    openDeleteConfirm(
      `Delete this favorite from ${where === "team" ? "Team" : "Individual"}?`,
      async () => {
        try {
          if (where === "team") {
            if (!activeTeamId) return;
            const ref = doc(db, "teams", activeTeamId);
            const existing = Array.isArray(team.favorites) ? team.favorites : [];
            const k = favoriteKey(f);
            const next = existing.filter((x) => favoriteKey(x) !== k);

            await updateDoc(ref, { favorites: next });

            setTeam((prev) => ({
              ...prev,
              favorites: next,
            }));

            setAlert({ type: "success", message: "Deleted from Team." });
          } else {
            const ref = doc(db, "users", userData.uid);
            const existing = Array.isArray(personal.favorites) ? personal.favorites : [];
            const k = favoriteKey(f);
            const next = existing.filter((x) => favoriteKey(x) !== k);

            await updateDoc(ref, { favorites: next });

            setPersonal((prev) => ({
              ...prev,
              favorites: next,
            }));

            setAlert({ type: "success", message: "Deleted from Individual." });
          }

          closeDeleteConfirm();
        } catch (e) {
          console.warn("Failed to delete favorite:", e);
          setAlert({ type: "error", message: "Failed to delete favorite." });
          throw e;
        }
      }
    );
  };

  // render decision (no early returns before hooks)
  let content = null;

  if (!userData && !authLoading) {
    return <LoginTeamGate />;
  } else if (userData && !authLoading && !activeTeamId) {
    return <LoginTeamGate />;
  } else if (authLoading || loadingData) {
    content = <p>Loading…</p>;
  } else {
    const centerTitle = activePane === "personal" ? leftTitle : rightTitle;

    const highlightsCount = activeData?.highlights?.length || 0;
    const favoritesCount = activeData?.favorites?.length || 0;

    const highlightSectionClass =
      highlightsCount === 0 ? "GuideLibrarySection GuideLibrarySection--compact" : "GuideLibrarySection";
    const favoriteSectionClass =
      favoritesCount === 0 ? "GuideLibrarySection GuideLibrarySection--compact" : "GuideLibrarySection";

    content = (
      <div className="GuideLibraryPage">
        {alert && (
          <CustomAlert
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert(null)}
            position="top"
          />
        )}

        {deleteConfirm.open && (
          <DeleteHighlightConfirm
            message={deleteConfirm.message}
            onCancel={closeDeleteConfirm}
            onConfirm={deleteConfirm.onConfirm}
          />
        )}

        <div className="GuideLibraryBook">
          {/* LEFT SIDEBAR */}
          <aside className="GuideLibrarySidebar GuideLibrarySidebar--left">
            <div className="GuideLibrarySidebarHeader">
              <h2 className={`GuideLibrarySidebarTitle ${activePane === "personal" ? "is-open" : ""}`}>
                {leftTitle}
              </h2>
            </div>

            <div className="GuideLibrarySidebarBody">
              {personalLocations.length === 0 ? (
                <div className="GuideLibraryEmptySide">
                  <p>No saved locations yet.</p>
                </div>
              ) : (
                <ul className="GuideLibraryStickyList">
                  {personalLocations.map((loc) => {
                    const isActive = activePane === "personal" && selectedPersonalLocation === loc.location;
                    return (
                      <li key={loc.location} className="GuideLibraryStickyRow">
                        <button
                          className={`GuideLibraryStickyNote ${isActive ? "is-active" : ""}`}
                          onClick={() => {
                            setActivePane("personal");
                            setSelectedPersonalLocation(loc.location);
                          }}
                          title={loc.location}
                          type="button"
                        >
                          <span className="GuideLibraryStickyText">{loc.location}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* CENTER CONTENT */}
          <main className="GuideLibraryCenter">
            <div className="GuideLibraryCenterHeader">
              <div className="GuideLibraryCenterHeaderTop">
                <h1 className="GuideLibraryCenterTitle is-open">{centerTitle}</h1>
              </div>

              <div className="GuideLibraryCenterSub">
                <span className="GuideLibraryCenterSubLabel">Selected location:</span>
                <span className="GuideLibraryCenterSubValue">{activeLocation || "None"}</span>
              </div>
            </div>

            <div className="GuideLibraryCenterBody">
              {!activeLocation ? (
                <div className="GuideLibraryEmptyCenter">
                  <h3>No location selected</h3>
                  <p>Select a sticky note on the left or right sidebar.</p>
                </div>
              ) : !activeData ? (
                <div className="GuideLibraryEmptyCenter">
                  <h3>Nothing saved here yet</h3>
                  <p>Try selecting a different location.</p>
                </div>
              ) : (
                <div className="GuideLibraryResults GuideLibraryResults--twoCol">
                  {/* Highlights */}
                  <section className={highlightSectionClass}>
                    <div className="GuideLibrarySectionHeader">
                      <h3 className="GuideLibrarySectionTitle">Highlights</h3>
                      <span className="GuideLibrarySectionCount">{highlightsCount}</span>
                    </div>

                    {highlightsCount === 0 ? (
                      <p className="GuideLibraryMuted">No highlights for this location.</p>
                    ) : (
                      <ul className="GuideLibraryCardList">
                        {activeData.highlights.map((h, idx) => {
                          const m = membership.highlight(h);
                          const origin = getOriginLabel(m);
                          const notes = getNotes("highlight", m.key);

                          const isEditorOpen =
                            noteUI.openFor?.type === "highlight" && noteUI.openFor?.key === m.key;

                          return (
                            <li
                              key={h.id || `${h.containerId || "hl"}-${idx}`}
                              className="GuideLibraryCard"
                            >
                              <div className="GuideLibraryCardTop">
                                <span
                                  className="GuideLibraryHighlightSwatch"
                                  style={{ backgroundColor: h.color || "#ffd54a" }}
                                />
                                <span className="GuideLibraryCardType">Highlight</span>

                                <span className="GuideLibraryOriginBadge" title={origin}>
                                  {origin}
                                </span>
                              </div>

                              <p className="GuideLibraryCardText">{h.excerpt}</p>

                              <div className="GuideLibraryCardActions GuideLibraryCardActions--row">
                                <button
                                  className="GuideLibraryActionBtn"
                                  onClick={() => handleNavigate(h.location, h.containerId)}
                                  type="button"
                                >
                                  Go to spot
                                </button>

                                <button
                                  className="GuideLibraryActionBtn"
                                  onClick={() =>
                                    openNoteEditor({
                                      type: "highlight",
                                      targetKey: m.key,
                                      defaultSaveTo: activePane === "team" ? "team" : "personal",
                                    })
                                  }
                                  type="button"
                                >
                                  Notes
                                </button>

                                {m.inPersonal && (
                                  <button
                                    className="GuideLibraryActionBtn GuideLibraryActionBtn--danger"
                                    onClick={() => deleteHighlightFrom("personal", h)}
                                    type="button"
                                  >
                                    Delete
                                  </button>
                                )}
                                {m.inTeam && (
                                  <button
                                    className="GuideLibraryActionBtn GuideLibraryActionBtn--danger"
                                    onClick={() => deleteHighlightFrom("team", h)}
                                    type="button"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>

                              {/* Notes list */}
                              {notes.length > 0 && (
                                <div className="GuideLibraryNotes">
                                  <div className="GuideLibraryNotesHeader">
                                    <span className="GuideLibraryNotesTitle">Notes</span>
                                    <span className="GuideLibraryNotesCount">{notes.length}</span>
                                  </div>

                                  <ul className="GuideLibraryNotesList">
                                    {notes.map((n) => (
                                      <li key={n.id} className="GuideLibraryNoteItem">
                                        <div className="GuideLibraryNoteMeta">
                                          <span className="GuideLibraryNoteAuthor">{n.createdBy || "Unknown"}</span>
                                          <span className="GuideLibraryNoteSource">
                                            {n._source === "team" ? "Team" : "Individual"}
                                          </span>
                                        </div>

                                        <p className="GuideLibraryNoteText">{n.text}</p>

                                        <div className="GuideLibraryNoteActions">
                                          <button
                                            className="GuideLibraryMiniAction"
                                            type="button"
                                            onClick={() => openEditNote(n)}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="GuideLibraryMiniAction danger"
                                            type="button"
                                            onClick={() => deleteNote(n)}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Note editor */}
                              {isEditorOpen && (
                                <div className="GuideLibraryNoteEditor">
                                  <div className="GuideLibraryNoteEditorTop">
                                    <div className="GuideLibraryNoteEditorTitle">
                                      {noteUI.editingNoteId ? "Edit note" : "Add note"}
                                    </div>

                                    <div className="GuideLibraryNoteEditorControls">
                                      <label className="GuideLibrarySelectLabel">
                                        Save to:
                                        <select
                                          className="GuideLibrarySelect"
                                          value={noteUI.saveTo}
                                          onChange={(e) =>
                                            setNoteUI((s) => ({ ...s, saveTo: e.target.value }))
                                          }
                                        >
                                          <option value="personal">Individual</option>
                                          <option value="team" disabled={!canSaveToTeam}>
                                            Team
                                          </option>
                                        </select>
                                      </label>

                                      <button
                                        className="GuideLibraryMiniAction app-cancel-control"
                                        type="button"
                                        onClick={closeNoteEditor}
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </div>

                                  <div className="GuideLibraryMentionWrap" ref={mentionAnchorRef}>
                                    <textarea
                                      ref={noteTextareaRef}
                                      className="GuideLibraryNoteTextarea"
                                      placeholder="Write a note… (max 425 chars). Use @ to tag."
                                      value={noteUI.text}
                                      onChange={(e) => onNoteTextChange(e.target.value)}
                                      maxLength={425}
                                    />

                                    <MentionMenu
                                      items={mentionState.items}
                                      open={mentionState.open}
                                      style={mentionState.style}
                                      activeIndex={mentionState.activeIndex}
                                      onHoverIndex={(i) =>
                                        setMentionState((s) => ({ ...s, activeIndex: i }))
                                      }
                                      onPick={pickMention}
                                    />
                                  </div>

                                  <div className="GuideLibraryNoteEditorBottom">
                                    <div className="GuideLibraryCharCount">
                                      {noteUI.text.length}/425
                                    </div>
                                    <button
                                      className="GuideLibraryActionBtn"
                                      type="button"
                                      onClick={saveNote}
                                    >
                                      {noteUI.editingNoteId ? "Save changes" : "Save note"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>

                  {/* Favorites */}
                  <section className={favoriteSectionClass}>
                    <div className="GuideLibrarySectionHeader">
                      <h3 className="GuideLibrarySectionTitle">Favorites</h3>
                      <span className="GuideLibrarySectionCount">{favoritesCount}</span>
                    </div>

                    {favoritesCount === 0 ? (
                      <p className="GuideLibraryMuted">No favorites for this location.</p>
                    ) : (
                      <ul className="GuideLibraryCardList">
                        {activeData.favorites.map((f, idx) => {
                          const m = membership.favorite(f);
                          const origin = getOriginLabel(m);
                          const notes = getNotes("favorite", m.key);

                          const isEditorOpen =
                            noteUI.openFor?.type === "favorite" && noteUI.openFor?.key === m.key;

                          return (
                            <li
                              key={f.headingId || `${f.pageLocation || "fav"}-${idx}`}
                              className="GuideLibraryCard"
                            >
                              <div className="GuideLibraryCardTop">
                                <span className="GuideLibraryCardType">Favorite</span>

                                <span className="GuideLibraryOriginBadge" title={origin}>
                                  {origin}
                                </span>
                              </div>

                              <p className="GuideLibraryCardText">{f.headingContent}</p>

                              <div className="GuideLibraryCardActions GuideLibraryCardActions--row">
                                <button
                                  className="GuideLibraryActionBtn"
                                  onClick={() => handleNavigate(f.pageLocation, f.headingId)}
                                  type="button"
                                >
                                  Go to heading
                                </button>

                                <button
                                  className="GuideLibraryActionBtn"
                                  onClick={() =>
                                    openNoteEditor({
                                      type: "favorite",
                                      targetKey: m.key,
                                      defaultSaveTo: activePane === "team" ? "team" : "personal",
                                    })
                                  }
                                  type="button"
                                >
                                  Notes
                                </button>

                                {m.inPersonal && (
                                  <button
                                    className="GuideLibraryActionBtn GuideLibraryActionBtn--danger"
                                    onClick={() => deleteFavoriteFrom("personal", f)}
                                    type="button"
                                  >
                                    Delete Individual
                                  </button>
                                )}
                                {m.inTeam && (
                                  <button
                                    className="GuideLibraryActionBtn GuideLibraryActionBtn--danger"
                                    onClick={() => deleteFavoriteFrom("team", f)}
                                    type="button"
                                  >
                                    Delete Team
                                  </button>
                                )}
                              </div>

                              {/* Notes list */}
                              {notes.length > 0 && (
                                <div className="GuideLibraryNotes">
                                  <div className="GuideLibraryNotesHeader">
                                    <span className="GuideLibraryNotesTitle">Notes</span>
                                    <span className="GuideLibraryNotesCount">{notes.length}</span>
                                  </div>

                                  <ul className="GuideLibraryNotesList">
                                    {notes.map((n) => (
                                      <li key={n.id} className="GuideLibraryNoteItem">
                                        <div className="GuideLibraryNoteMeta">
                                          <span className="GuideLibraryNoteAuthor">{n.createdBy || "Unknown"}</span>
                                          <span className="GuideLibraryNoteSource">
                                            {n._source === "team" ? "Team" : "Individual"}
                                          </span>
                                        </div>

                                        <p className="GuideLibraryNoteText">{n.text}</p>

                                        <div className="GuideLibraryNoteActions">
                                          <button
                                            className="GuideLibraryMiniAction"
                                            type="button"
                                            onClick={() => openEditNote(n)}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="GuideLibraryMiniAction danger"
                                            type="button"
                                            onClick={() => deleteNote(n)}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Note editor */}
                              {isEditorOpen && (
                                <div className="GuideLibraryNoteEditor">
                                  <div className="GuideLibraryNoteEditorTop">
                                    <div className="GuideLibraryNoteEditorTitle">
                                      {noteUI.editingNoteId ? "Edit note" : "Add note"}
                                    </div>

                                    <div className="GuideLibraryNoteEditorControls">
                                      <label className="GuideLibrarySelectLabel">
                                        Save to:
                                        <select
                                          className="GuideLibrarySelect"
                                          value={noteUI.saveTo}
                                          onChange={(e) =>
                                            setNoteUI((s) => ({ ...s, saveTo: e.target.value }))
                                          }
                                        >
                                          <option value="personal">Individual</option>
                                          <option value="team" disabled={!canSaveToTeam}>
                                            Team
                                          </option>
                                        </select>
                                      </label>

                                      <button
                                        className="GuideLibraryMiniAction app-cancel-control"
                                        type="button"
                                        onClick={closeNoteEditor}
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </div>

                                  <div className="GuideLibraryMentionWrap" ref={mentionAnchorRef}>
                                    <textarea
                                      ref={noteTextareaRef}
                                      className="GuideLibraryNoteTextarea"
                                      placeholder="Write a note… (max 425 chars). Use @ to tag."
                                      value={noteUI.text}
                                      onChange={(e) => onNoteTextChange(e.target.value)}
                                      maxLength={425}
                                    />

                                    <MentionMenu
                                      items={mentionState.items}
                                      open={mentionState.open}
                                      style={mentionState.style}
                                      activeIndex={mentionState.activeIndex}
                                      onHoverIndex={(i) =>
                                        setMentionState((s) => ({ ...s, activeIndex: i }))
                                      }
                                      onPick={pickMention}
                                    />
                                  </div>

                                  <div className="GuideLibraryNoteEditorBottom">
                                    <div className="GuideLibraryCharCount">
                                      {noteUI.text.length}/425
                                    </div>
                                    <button
                                      className="GuideLibraryActionBtn"
                                      type="button"
                                      onClick={saveNote}
                                    >
                                      {noteUI.editingNoteId ? "Save changes" : "Save note"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                </div>
              )}
            </div>
          </main>

          {/* RIGHT SIDEBAR */}
          <aside className="GuideLibrarySidebar GuideLibrarySidebar--right">
            <div className="GuideLibrarySidebarHeader">
              <h2 className={`GuideLibrarySidebarTitle ${activePane === "team" ? "is-open" : ""}`}>
                {rightTitle}
              </h2>
            </div>

            <div className="GuideLibrarySidebarBody">
              {!activeTeamId ? (
                <div className="GuideLibraryEmptySide">
                  <p>No active team.</p>
                  <button
                    className="GuideLibraryMiniBtn"
                    onClick={() =>
                      setAlert({ message: "Join a Team to view the Team Library.", type: "warning" })
                    }
                    type="button"
                  >
                    How to fix
                  </button>
                </div>
              ) : teamLocations.length === 0 ? (
                <div className="GuideLibraryEmptySide">
                  <p>No saved locations yet.</p>
                </div>
              ) : (
                <ul className="GuideLibraryStickyList">
                  {teamLocations.map((loc) => {
                    const isActive = activePane === "team" && selectedTeamLocation === loc.location;
                    return (
                      <li key={loc.location} className="GuideLibraryStickyRow">
                        <button
                          className={`GuideLibraryStickyNote ${isActive ? "is-active" : ""}`}
                          onClick={() => {
                            setActivePane("team");
                            setSelectedTeamLocation(loc.location);
                          }}
                          title={loc.location}
                          type="button"
                        >
                          <span className="GuideLibraryStickyText">{loc.location}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <>
      {content}
      {!hideFooter ? <Footer /> : null}
    </>
  );
}
